import { Link, useNavigate } from "@tanstack/react-router";
import { t } from "i18next";
import {
  LogOutIcon,
  PanelLeftClose,
  PanelLeftOpen,
  Shield,
} from "lucide-react";
import { useState } from "react";
import { AppConfig } from "@/common/appConfig";
import { bottomItemsSidebar, topItemsSidebar } from "@/common/constants";
import { FeedbackMenuItem } from "@/components/ui/feedback/FeedbackMenuItem";
import { SignOutRecordingsDialog } from "@/components/ui/offline-recording/SignOutRecordingsDialog";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { useSidebar } from "@/components/ui/sidebar/useSidebar";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { WhatsNewMenuItem } from "@/components/ui/whats-new/WhatsNewMenuItem";
import { useAuthStore } from "@/store/auth.store";
import { useNativeAuthStore } from "@/store/native-auth.store";
import { useOfflineRecordingStore } from "@/store/offlineRecording.store";
import { useSidebarActiveItems } from "./hooks/useSidebarActiveItems";
import { useSidebarRail } from "./rail";

// Icon-only rail: 44px centred targets, labels kept for screen readers (and
// shown as tooltips on hover or focus).
const RAIL_BUTTON_CLASS =
  "group-data-[collapsible=icon]:size-11! group-data-[collapsible=icon]:p-0! group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:justify-center [&>span]:group-data-[collapsible=icon]:sr-only";

interface LayoutProps {
  children?: React.ReactNode;
}

export const AppSidebar: React.FC<LayoutProps> = () => {
  const { signOut } = useAuthStore.getState();
  const role = useNativeAuthStore((state) => state.session?.role);
  const navigate = useNavigate();
  const handleSignOut = async (): Promise<void> => {
    await signOut();
    await navigate({ to: "/" });
  };
  // Offline recording: never sign out silently over unsent recordings.
  const [signOutGuard, setSignOutGuard] = useState(false);
  const [signOutBusy, setSignOutBusy] = useState(false);
  const unsentRecordings = useOfflineRecordingStore(
    (state) => state.recordings.length,
  );

  const rail = useSidebarRail();
  const { isMobile } = useSidebar();
  const iconOnly = !isMobile && rail.collapsed;
  const topActiveItems = useSidebarActiveItems(topItemsSidebar);
  const bottomActiveItems = useSidebarActiveItems(bottomItemsSidebar);

  return (
    <Sidebar className="group-data-[side=left]:border-none" collapsible="icon">
      <SidebarHeader>
        {iconOnly ? (
          <div className="flex w-full items-center justify-center py-8">
            <img
              alt="ROSC"
              height={36}
              src={`${import.meta.env.BASE_URL}images/logos/logo.svg`}
              width={36}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center content-center w-full p-8">
            <img
              alt="ROSC"
              height={56}
              src={`${import.meta.env.BASE_URL}images/logos/logo-full.svg`}
              width={165}
            />
          </div>
        )}
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {topActiveItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    className={RAIL_BUTTON_CLASS}
                    disabled={item.disabled}
                    isActive={item.isActive}
                    tooltip={t(item.title as never)}
                  >
                    <Link to={item.url}>
                      {typeof item.icon === "string" ? (
                        <img
                          alt={item.title}
                          height={15}
                          src={item.icon}
                          width={15}
                        />
                      ) : (
                        <item.icon
                          aria-hidden="true"
                          className="w-auto! h-6!"
                        />
                      )}

                      <span>{t(item.title as any)}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <div className="flex justify-between items-center p-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
          <span className="group-data-[collapsible=icon]:sr-only">
            {t("sidebar.themeToggle.darkMode")}
          </span>
          <ThemeToggle label={t("sidebar.themeToggle.darkMode")} />
        </div>
        <SidebarSeparator />
        <SidebarContent>
          <SidebarGroup className="group-data-[collapsible=icon]:px-0">
            <SidebarGroupContent>
              <SidebarMenu>
                {role === "admin" && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      className={RAIL_BUTTON_CLASS}
                      tooltip="Admin"
                    >
                      <Link to="/admin">
                        <Shield aria-hidden="true" className="w-auto! h-6!" />
                        <span>Admin</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {AppConfig.enableWhatsNew && (
                  <WhatsNewMenuItem className={RAIL_BUTTON_CLASS} tooltip />
                )}
                <FeedbackMenuItem className={RAIL_BUTTON_CLASS} tooltip />
                {bottomActiveItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      className={RAIL_BUTTON_CLASS}
                      disabled={item.disabled}
                      isActive={item.isActive}
                      tooltip={t(item.title as never)}
                    >
                      <Link to={item.url}>
                        {typeof item.icon === "string" ? (
                          <img
                            alt={item.title}
                            height={15}
                            src={item.icon}
                            width={15}
                          />
                        ) : (
                          <item.icon
                            aria-hidden="true"
                            className="w-auto! h-6!"
                          />
                        )}

                        <span>{t(item.title as any)}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    className={RAIL_BUTTON_CLASS}
                    tooltip={t("sidebar.logout")}
                  >
                    <Link
                      to="/"
                      onClick={(event) => {
                        if (unsentRecordings > 0) {
                          event.preventDefault();
                          setSignOutGuard(true);
                          return;
                        }
                        void handleSignOut();
                      }}
                    >
                      <LogOutIcon className="w-auto! h-6!" />
                      <span>{t("sidebar.logout")}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                {rail.canCollapse && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      aria-expanded={!rail.collapsed}
                      className={RAIL_BUTTON_CLASS}
                      data-testid="sidebar-rail-toggle"
                      tooltip={t(
                        rail.collapsed
                          ? "sidebar.expandMenu"
                          : "sidebar.collapseMenu",
                      )}
                      type="button"
                      onClick={rail.toggle}
                    >
                      {rail.collapsed ? (
                        <PanelLeftOpen
                          aria-hidden="true"
                          className="w-auto! h-6!"
                        />
                      ) : (
                        <PanelLeftClose
                          aria-hidden="true"
                          className="w-auto! h-6!"
                        />
                      )}
                      <span>
                        {t(
                          rail.collapsed
                            ? "sidebar.expandMenu"
                            : "sidebar.collapseMenu",
                        )}
                      </span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </SidebarFooter>
      <SignOutRecordingsDialog
        busy={signOutBusy}
        count={unsentRecordings}
        open={signOutGuard}
        onCancel={() => setSignOutGuard(false)}
        onDelete={() => {
          setSignOutBusy(true);
          void useOfflineRecordingStore
            .getState()
            .discardAll()
            .catch(() => undefined)
            .then(handleSignOut)
            .finally(() => {
              setSignOutBusy(false);
              setSignOutGuard(false);
            });
        }}
        onKeep={() => {
          setSignOutGuard(false);
          void handleSignOut();
        }}
      />
    </Sidebar>
  );
};
